/**
 * Native (Capacitor) bootstrap.
 *
 * - Marks `body.capacitor-app` to enable safe-area CSS rules.
 * - Configures status bar.
 * - Handles deeplinks: `?invite=CODE` and `?ref=CODE` are persisted to localStorage
 *   exactly like the web flow (see Landing.jsx + AuthCallback.jsx).
 * - Hides the splash screen once the app is mounted.
 * - Registers for push notifications (Android FCM + iOS APNs) and posts the
 *   device token to the backend `/api/devices/register`.
 *
 * No-op when running in a regular browser.
 */
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { PushNotifications } from "@capacitor/push-notifications";
import axios from "axios";

const REF_RE = /^[A-Z0-9]{4,16}$/;

function persistRefAndInviteFromUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const ref = (u.searchParams.get("ref") || "").toUpperCase();
    const invite = (u.searchParams.get("invite") || "").toUpperCase();
    if (ref && REF_RE.test(ref)) {
      localStorage.setItem("lf_pending_ref", ref);
    }
    if (invite && REF_RE.test(invite)) {
      localStorage.setItem("lf_pending_invite", invite);
    }
  } catch (err) {
    // Malformed URL — deeplinks from legacy OSes can arrive raw. Non-fatal.
    // eslint-disable-next-line no-console
    console.debug("[capacitor] deeplink URL parse failed:", err?.message);
  }
}

async function registerPush(apiBase) {
  try {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    await PushNotifications.register();
    PushNotifications.addListener("registration", async (token) => {
      try {
        await axios.post(
          `${apiBase}/api/devices/register`,
          {
            token: token.value,
            platform: Capacitor.getPlatform(),
          },
          { withCredentials: true }
        );
      } catch (err) {
        // Swallow — backend may be unreachable on first launch.
        // The token will be re-sent on next app open.
        // eslint-disable-next-line no-console
        console.warn("[push] register failed:", err?.message);
      }
    });
    PushNotifications.addListener("registrationError", (err) => {
      // eslint-disable-next-line no-console
      console.warn("[push] registrationError:", err);
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[push] init failed:", err?.message);
  }
}

export async function initCapacitor(apiBase) {
  if (!Capacitor.isNativePlatform()) return;

  document.body.classList.add("capacitor-app");

  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#0F172A" });
  } catch (err) {
    // iOS without status bar overlay OR older Android — platform capability
    // mismatch, not a real failure. Log at debug level for diagnostics.
    // eslint-disable-next-line no-console
    console.debug("[capacitor] status bar config unavailable:", err?.message);
  }

  // Listen for deeplinks while the app is running (clicks on
  // https://leaseflowr.com/?invite=XXX outside the app).
  App.addListener("appUrlOpen", (event) => {
    if (event?.url) persistRefAndInviteFromUrl(event.url);
  });

  // Cold-start deeplink (app launched FROM a deeplink).
  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) persistRefAndInviteFromUrl(launch.url);
  } catch (err) {
    // No cold-start deeplink — the user opened the app normally. Common path.
    // eslint-disable-next-line no-console
    console.debug("[capacitor] no launch URL:", err?.message);
  }

  // Hide splash once the bundle has booted.
  setTimeout(() => {
    SplashScreen.hide().catch(() => {});
  }, 600);

  // Register for push (best-effort, async).
  registerPush(apiBase);
}
